import { Action, Reducer } from 'redux'
import { ICarrierLanesState, RATE_ACTION_TYPE } from 'types/carrier'
import {
  CARRIER_LANES_ACTION_TYPES,
  APPROVE_LANE_ACTION_TYPES,
  REMOVE_LANE_MODAL_ACTION_TYPES,
  REMOVE_LANES_ACTION_TYPES,
  LANE_MODAL_ACTION_TYPES,
  SAVE_LANES_ACTION_TYPES,
  LANE_CREATED_MODAL_ACTION_TYPES,
} from 'apps/carrier/utils/actions/carrierLanes'
import { IRate } from 'apps/marketplace/store/contractLanes/types'
import { linkRadiusRates } from 'apps/marketplace/utils/helpers/rates'

const {
  CARRIER_LANES_FAILURE,
  CARRIER_LANES_REQUEST,
  CARRIER_LANES_SUCCESS,
} = CARRIER_LANES_ACTION_TYPES

const {
  APPROVE_LANE_FAILURE,
  APPROVE_LANE_REQUEST,
  APPROVE_LANE_SUCCESS,
} = APPROVE_LANE_ACTION_TYPES

const { REMOVE_LANE_MODAL_CLOSE, REMOVE_LANE_MODAL_OPEN } = REMOVE_LANE_MODAL_ACTION_TYPES

const {
  REMOVE_LANES_FAILURE,
  REMOVE_LANES_REQUEST,
  REMOVE_LANES_SUCCESS,
} = REMOVE_LANES_ACTION_TYPES

const { LANE_MODAL_CLOSE, LANE_MODAL_OPEN } = LANE_MODAL_ACTION_TYPES

const { SAVE_LANES_FAILURE, SAVE_LANES_REQUEST, SAVE_LANES_SUCCESS } = SAVE_LANES_ACTION_TYPES

const { LANE_CREATED_MODAL_CLOSE, LANE_CREATED_MODAL_OPEN } = LANE_CREATED_MODAL_ACTION_TYPES

interface IErrorAction extends Action<string> {
  error: string
}
interface ISuccessAction extends Action<string> {
  payload: {
    carriercontractlanes: IRate[]
  }
}
interface IActionSuccessAction extends Action<string> {
  payload: {
    contractId: number
    approvedById?: number
    approvedAt?: string
    requestData: {
      body: {
        contractLanes: { carrierContractLaneId: number }[]
      }
    }
  }
}
interface ILaneAction extends Action<string> {
  payload: {
    lane: IRate
  }
}

type CarrierLanesAction =
  | Action<string>
  | IErrorAction
  | ISuccessAction
  | IActionSuccessAction
  | ILaneAction

const defaultState: ICarrierLanesState = {
  fetching: false,
  lanes: {
    reserved: [],
    alerts: [],
  },
  actionInProgress: false,
  showRemoveModal: false,
  showEditModal: false,
  showCreatedModal: false,
}

const groupLanes = (lanes: IRate[]) => {
  const radiusLanes = lanes.filter(lane => !lane.delivery)
  linkRadiusRates(radiusLanes)
  return {
    reserved: lanes.filter(({ actionType }) => actionType === RATE_ACTION_TYPE.reserved),
    alerts: lanes.filter(({ actionType }) => actionType === RATE_ACTION_TYPE.alert),
  }
}

const carrierLanesReducer: Reducer<ICarrierLanesState, CarrierLanesAction> = (
  state = defaultState,
  action
) => {
  switch (action?.type) {
    case CARRIER_LANES_REQUEST: {
      return {
        ...state,
        fetching: true,
        error: undefined,
      }
    }

    case CARRIER_LANES_SUCCESS: {
      const { carriercontractlanes } = (action as ISuccessAction).payload
      return {
        ...state,
        fetching: false,
        lanes: groupLanes(carriercontractlanes),
      }
    }

    case CARRIER_LANES_FAILURE: {
      return {
        ...state,
        fetching: false,
        error: (action as IErrorAction).error,
      }
    }

    case APPROVE_LANE_REQUEST:
    case REMOVE_LANES_REQUEST: {
      return {
        ...state,
        actionInProgress: true,
        actionError: undefined,
      }
    }

    case APPROVE_LANE_SUCCESS: {
      const { approvedAt, approvedById, contractId } = (action as IActionSuccessAction).payload
      return {
        ...state,
        actionInProgress: false,
        lanes: {
          ...state.lanes,
          reserved: state.lanes.reserved.map(lane =>
            lane.contractId === contractId ? { ...lane, approvedAt, approvedById } : lane
          ),
        },
      }
    }

    case REMOVE_LANES_SUCCESS: {
      const removedIds = (action as IActionSuccessAction).payload.requestData.body.contractLanes.map(
        entry => entry.carrierContractLaneId
      )
      return {
        ...state,
        actionInProgress: false,
        showRemoveModal: false,
        targetLane: undefined,
        lanes: groupLanes(
          [...state.lanes.reserved, ...state.lanes.alerts].filter(
            lane => !removedIds.includes(lane.contractId)
          )
        ),
      }
    }

    case APPROVE_LANE_FAILURE:
    case REMOVE_LANES_FAILURE: {
      return {
        ...state,
        actionInProgress: false,
        actionError: (action as IErrorAction).error,
      }
    }

    case REMOVE_LANE_MODAL_OPEN: {
      return {
        ...state,
        showRemoveModal: true,
        targetLane: (action as ILaneAction).payload.lane,
      }
    }

    case REMOVE_LANE_MODAL_CLOSE: {
      return {
        ...state,
        showRemoveModal: false,
        targetLane: undefined,
      }
    }

    case LANE_MODAL_OPEN: {
      return {
        ...state,
        showEditModal: true,
        actionError: undefined,
        targetLane: (action as ILaneAction).payload.lane,
      }
    }

    case LANE_MODAL_CLOSE: {
      return {
        ...state,
        showEditModal: false,
        targetLane: undefined,
      }
    }

    case SAVE_LANES_REQUEST: {
      return {
        ...state,
        actionInProgress: true,
      }
    }

    case SAVE_LANES_SUCCESS: {
      return {
        ...state,
        actionInProgress: false,
        showEditModal: false,
        targetLane: undefined,
      }
    }

    case SAVE_LANES_FAILURE: {
      return {
        ...state,
        actionInProgress: false,
        actionError: (action as IErrorAction).error,
      }
    }

    case LANE_CREATED_MODAL_OPEN: {
      return {
        ...state,
        showCreatedModal: true,
      }
    }

    case LANE_CREATED_MODAL_CLOSE: {
      return {
        ...state,
        showCreatedModal: false,
      }
    }

    default:
      return state
  }
}

export default carrierLanesReducer
